import React, { useCallback, useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import MetaTags from 'react-meta-tags';
import isEmpty from 'lodash/isEmpty';

import GlobalStyles from './styles';
import { ROUTE_TYPES } from './constants';
import { authData, generateRoutes } from './utils';
import API from './api';

const { ALLOW_IF_LOGGEDIN, ALLOW_IF_NOT_LOGGEDIN } = ROUTE_TYPES;

/**
 * Route wrapper is defined outside App to prevent unnecessary
 * component remounts when App re-renders.
 */
const ProcessedRoute = ({
  component: Component,
  routeType,
  moduleName,
  permissionRequired,
  appData,
  isLoggedIn,
  accountsPermissions,
  ...rest
}) => {
  if (isEmpty(routeType)) {
    return <Component {...rest} appData={appData} />;
  }

  if (isLoggedIn && accountsPermissions === null) {
    authData.clear();
    window.location.href = '/login';
    return null;
  }

  const isRouteAllowed =
    (routeType === ALLOW_IF_NOT_LOGGEDIN && !isLoggedIn) ||
    (routeType === ALLOW_IF_LOGGEDIN && isLoggedIn);

  if (!isRouteAllowed) {
    return (
      <Navigate
        to={routeType === ALLOW_IF_LOGGEDIN ? '/login' : '/'}
        replace
      />
    );
  }

  const hasPermission =
    isEmpty(moduleName) ||
    (
      !isEmpty(accountsPermissions?.[moduleName]) &&
      accountsPermissions[moduleName].includes(permissionRequired)
    );

  if (!hasPermission) {
    return <Navigate to="/" replace />;
  }

  return (
    <Component
      accountsPermissions={
        !isEmpty(moduleName)
          ? accountsPermissions?.[moduleName]
          : undefined
      }
      routeType={!isEmpty(routeType) ? routeType : undefined}
      moduleName={!isEmpty(moduleName) ? moduleName : undefined}
      permissionRequired={
        !isEmpty(permissionRequired) ? permissionRequired : undefined
      }
      appData={appData}
      {...rest}
    />
  );
};

const App = () => {
  const routesList = generateRoutes();

  const { isLoggedIn, accountsPermissions } = authData.get();

  const [appData, setAppData] = useState({
    isLoading: true,
    appName: '',
    appLogo: '',
    appFavicon: '',
    accessType: 'V1',
    appBackgroundColor: '',
    appPrimaryColor: '',
    appSecondaryColor: '',
    appTertiaryColor: '',
    appTableHeaderColor: '',
  });

  const theme = {
    colors: {
      backgroundColor: appData.appBackgroundColor || '#ffffff',
      primaryColor: appData.appPrimaryColor || '#0095ff',
      secondaryColor: appData.appSecondaryColor || '#0078ce',
      tertiaryColor: appData.appTertiaryColor || '#0067b0',
      tableHeaderColor: appData.appTableHeaderColor || '#545454',
    },
  };

  const readMetaData = useCallback(async () => {
    try {
      const { data } = await API.get('/meta');

      const metaData = {
        appName: data?.appName || '',
        appLogo: data?.logo || '',
        appFavicon: data?.favicon || '',
        accessType: data?.accessType || 'V1',
        appBackgroundColor: data?.appBackgroundColor || '',
        appPrimaryColor: data?.appPrimaryColor || '',
        appSecondaryColor: data?.appSecondaryColor || '',
        appTertiaryColor: data?.appTertiaryColor || '',
        appTableHeaderColor: data?.appTableHeaderColor || '',
      };

      authData.set('appData', JSON.stringify(metaData));

      setAppData({
        isLoading: false,
        ...metaData,
      });
    } catch (error) {
      console.error('Failed to load application metadata:', error);

      setAppData((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  }, []);

  useEffect(() => {
    readMetaData();
  }, [readMetaData]);

  if (appData.isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <MetaTags>
        <title>{appData.appName}</title>
        <meta name="description" content={appData.appName} />
        <meta property="og:title" content={appData.appName} />
        <meta property="og:image" content={appData.appLogo} />
        <link rel="icon" href={appData.appFavicon} />
        <link rel="apple-touch-icon" href={appData.appLogo} />
      </MetaTags>

      <ThemeProvider theme={theme}>
        <GlobalStyles />

        <Router>
          <Routes>
            {routesList.map((item) => {
              const { path, ...rest } = item;

              if (!path) {
                return null;
              }

              return (
                <Route
                  path={path}
                  element={
                    <ProcessedRoute
                      {...rest}
                      appData={appData}
                      isLoggedIn={isLoggedIn}
                      accountsPermissions={accountsPermissions}
                    />
                  }
                  key={path}
                />
              );
            })}
          </Routes>
        </Router>
      </ThemeProvider>
    </>
  );
};

export default App;